import { type FC, type KeyboardEvent, type RefObject, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router'
import classnames from 'classnames'
import { TooltipEmailDomain } from 'shared/ui/Tooltip/ui/TooltipEmailDomain/TooltipEmailDomain'
import {
  Avatar,
  Button,
  Dropdown,
  DropdownContent,
  EmptyState,
  Icon,
  SpinnerLoader,
  Typography,
} from 'shared/ui'
import {
  makeTextFieldActionTooltip,
  makeTextFieldAutoFocus,
  makeTextFieldClear,
  makeTextFieldInteractions,
  makeTextFieldTooltip,
  TextField,
} from 'shared/ui/TextField'
import {
  getAriaLabel,
  getLabelAsNumberInternationalFormat,
  removeSpacesFromNumericString,
} from 'shared/lib'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type IUser } from 'entities/Users'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import {
  type ContactInfoFormStore,
  MainFiledTypes,
} from 'widgets/ContactsInfoForm/store/contactInfoFormStore'
import { CustomFieldField } from 'widgets/ContactsInfoForm/ui/CustomFieldField/CustomFieldField'
import { PhoneFieldSearch } from './PhoneFieldSearch'
import { OrganizationMember } from './OrganizationMember'
import styles from './styles.module.scss'

type IContactsInfoFormProps = {
  store: ContactInfoFormStore
}

export const ContactsInfoForm: FC<IContactsInfoFormProps> = observer(({ store }) => {
  const {
    isVariantCreateContactModal,
    setIsFirstEditMode,
    isFirstEditMode,
    hasCustomFields,
    getVisibleCustomFieldsList,
    mainFields,
    searchOwner,
    errors,
    updatingFields,
    hasMembers,
    membersList,
    membersCount,
    handleSaveOwner,
    clearSearchOwner,
    handleSearchOwner,
    handleSaveMainField,
    handleUpdateMainField,
    number,
    phoneFieldSearchStore,
    contact,
    loadingCustomField,
    blockViewKey,
    zIndex,
  } = store
  const numberInputRef = useRef<HTMLInputElement>(null)
  const firstNameInputRef = useRef<HTMLInputElement>(null)
  const lastNameInputRef = useRef<HTMLInputElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const { usersStore, userPermissionsStore } = useEntitiesContext()
  const { toggleBlockView, isOpenBlock } = contactsDetailsGlobalStore
  const { pathname } = useLocation()
  const [firstEditInputNumber, setFirstEditInputNumber] = useState(0)
  const isContactPage = pathname.includes('contacts')

  const checkFirstEditMode = (isEnterPressed: boolean, nextActiveInputNumber?: number) => {
    if (!nextActiveInputNumber || !isEnterPressed) {
      setIsFirstEditMode(false)

      return
    }

    setFirstEditInputNumber(nextActiveInputNumber)
  }

  if (!contact) return null

  const saveMainField = (field: MainFiledTypes, contact: Contact) => {
    return handleSaveMainField(field, contact)
  }

  type MainFieldValueKey = Exclude<MainFiledTypes, MainFiledTypes.owner>

  const createMainFieldKeyDown =
    (field: MainFieldValueKey, inputRef: RefObject<HTMLInputElement | null>) =>
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        saveMainField(field, contact).then((canBlur) => {
          if (canBlur) {
            inputRef.current?.blur()
          }
        })
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        const initialValue = contact[field] || ''
        handleUpdateMainField(field, initialValue, true)
        inputRef.current?.blur()
      }
    }

  const isVisibleOnlyCustomFields = !isContactPage && !isVariantCreateContactModal

  const isNewContactMode = contact.id === -1
  const disabledDefaultField = isNewContactMode
  const ariaLabelField = getAriaLabel('Field')

  return (
    <div className={styles.wrap}>
      {isNewContactMode && (
        <div className={styles.contactDetailsField} aria-label={ariaLabelField}>
          <PhoneFieldSearch
            store={phoneFieldSearchStore}
            value={number}
            isVariantCreateContactModal={isVariantCreateContactModal}
            onChange={(value) => {
              handleUpdateMainField(MainFiledTypes.number, value)
            }}
          />
        </div>
      )}
      {!isNewContactMode && (
        <div className={styles.field} aria-label={ariaLabelField}>
          <span className={styles.label} aria-label={getAriaLabel(ariaLabelField, 'Label')}>
            <div className={styles.icon} aria-label={getAriaLabel('FieldType', 'Phone')}>
              <Icon icon='phone' />
            </div>
            <Typography
              className={styles.name}
              variant='text-input-field-r'
              tag='div'
              ariaLabel={getAriaLabel(ariaLabelField, 'NamePhone')}
            >
              Phone
            </Typography>
          </span>
          <div className={styles.fieldContent}>
            <TextField
              className={classnames(styles.value, {
                [styles.modalView]: isVariantCreateContactModal,
              })}
              InputProps={{
                placeholder: 'Phone',
                ref: numberInputRef,
                onKeyDown: createMainFieldKeyDown(MainFiledTypes.number, numberInputRef),
              }}
              size='small'
              variant='outline'
              value={getLabelAsNumberInternationalFormat(number)}
              readOnly={updatingFields.has(MainFiledTypes.number)}
              disabled={updatingFields.has(MainFiledTypes.number)}
              onBlur={() => {
                saveMainField(MainFiledTypes.number, contact)
              }}
              onChange={(value) => {
                if (removeSpacesFromNumericString(value) === number) return

                const correctNumber = phoneFieldSearchStore.onChangeField(value, number)
                handleUpdateMainField(MainFiledTypes.number, correctNumber ?? '', true)
              }}
              onFocus={(_, current) => {
                if (!userPermissionsStore.getItem('can_create_contacts')) {
                  new FeatureRestrictedModalStore('can_create_contacts')
                  current?.blur()
                }
              }}
              rightActions={[
                makeTextFieldInteractions({
                  type: 'copy',
                  active: Boolean(mainFields.number),
                  zIndex: zIndex,
                }),
                makeTextFieldClear(),
              ]}
              mainActions={[
                makeTextFieldActionTooltip({
                  color: 'red',
                  placement: 'left',
                  active: errors.has('number'),
                  message: errors.get('number'),
                  onClick: () => errors.delete('number'),
                  zIndex: zIndex,
                }),
              ]}
              ariaLabel={getAriaLabel(ariaLabelField, 'Value')}
            />
          </div>
        </div>
      )}

      <div className={styles.field} aria-label={ariaLabelField}>
        <span
          className={classnames(styles.label, {
            [styles.disabled]: isNewContactMode,
          })}
          aria-label={getAriaLabel(ariaLabelField, 'Label')}
        >
          <div className={styles.icon} aria-label={getAriaLabel('FieldType', 'FirstName')}>
            <Icon icon='user' />
          </div>
          <Typography
            className={styles.name}
            variant='text-input-field-r'
            tag='div'
            ariaLabel={getAriaLabel(ariaLabelField, 'NameFirstName')}
          >
            First name
          </Typography>
        </span>
        <div className={styles.fieldContent}>
          <TextField
            className={classnames(styles.value, {
              [styles.modalView]: isVariantCreateContactModal,
            })}
            size='small'
            variant='outline'
            InputProps={{
              placeholder: 'First name',
              autoComplete: 'off',
              ref: firstNameInputRef,
              onKeyDown: createMainFieldKeyDown(MainFiledTypes.firstName, firstNameInputRef),
            }}
            value={mainFields.first_name}
            disabled={disabledDefaultField || updatingFields.has(MainFiledTypes.firstName)}
            onChange={(value) => handleUpdateMainField(MainFiledTypes.firstName, value, true)}
            onBlur={(_, { isTrusted }) => {
              checkFirstEditMode(!isTrusted, 1)
              saveMainField(MainFiledTypes.firstName, contact)
            }}
            rightActions={[
              makeTextFieldInteractions({
                type: 'copy',
                active: Boolean(mainFields.first_name),
                zIndex: zIndex,
              }),
              makeTextFieldClear(),
            ]}
            mainActions={[
              makeTextFieldAutoFocus({ withFocus: isFirstEditMode && firstEditInputNumber === 0 }),
              makeTextFieldTooltip({ zIndex: zIndex }),
              makeTextFieldActionTooltip({
                color: 'red',
                placement: 'left',
                active: errors.has('first_name'),
                message: errors.get('first_name'),
                onClick: () => errors.delete('first_name'),
                zIndex: zIndex,
              }),
            ]}
            ariaLabel={getAriaLabel(ariaLabelField, 'Value')}
          />
        </div>
      </div>

      <div className={styles.field} aria-label={ariaLabelField}>
        <span
          className={classnames(styles.label, {
            [styles.disabled]: isNewContactMode,
          })}
          aria-label={getAriaLabel(ariaLabelField, 'Label')}
        >
          <div className={styles.icon} aria-label={getAriaLabel('FieldType', 'LastName')}>
            <Icon icon='user' />
          </div>
          <Typography
            className={styles.name}
            variant='text-input-field-r'
            tag='div'
            ariaLabel={getAriaLabel(ariaLabelField, 'NameLastName')}
          >
            Last name
          </Typography>
        </span>
        <div className={styles.fieldContent}>
          <TextField
            className={classnames(styles.value, {
              [styles.modalView]: isVariantCreateContactModal,
            })}
            size='small'
            variant='outline'
            InputProps={{
              placeholder: 'Last name',
              autoComplete: 'off',
              ref: lastNameInputRef,
              onKeyDown: createMainFieldKeyDown(MainFiledTypes.lastName, lastNameInputRef),
            }}
            value={mainFields.last_name}
            disabled={disabledDefaultField || updatingFields.has(MainFiledTypes.lastName)}
            onChange={(value) => handleUpdateMainField(MainFiledTypes.lastName, value, true)}
            onBlur={(_, { isTrusted }) => {
              checkFirstEditMode(!isTrusted, 2)
              saveMainField(MainFiledTypes.lastName, contact)
            }}
            rightActions={[
              makeTextFieldInteractions({
                type: 'copy',
                active: Boolean(mainFields.last_name),
                zIndex: zIndex,
              }),
              makeTextFieldClear(),
            ]}
            mainActions={[
              makeTextFieldAutoFocus({ withFocus: isFirstEditMode && firstEditInputNumber === 1 }),
              makeTextFieldTooltip({ zIndex: zIndex }),
              makeTextFieldActionTooltip({
                color: 'red',
                placement: 'left',
                active: errors.has('last_name'),
                message: errors.get('last_name'),
                onClick: () => errors.delete('last_name'),
                zIndex: zIndex,
              }),
            ]}
            ariaLabel={getAriaLabel(ariaLabelField, 'Value')}
          />
        </div>
      </div>

      <div className={styles.field} aria-label={ariaLabelField}>
        <span
          className={classnames(styles.label, {
            [styles.disabled]: isNewContactMode,
          })}
          aria-label={getAriaLabel(ariaLabelField, 'Label')}
        >
          <div className={styles.icon} aria-label={getAriaLabel('FieldType', 'Email')}>
            <Icon icon='email' />
          </div>
          <Typography
            className={styles.name}
            variant='text-input-field-r'
            tag='div'
            ariaLabel={getAriaLabel(ariaLabelField, 'NameEmail')}
          >
            Email
          </Typography>
        </span>
        <div className={styles.fieldContent}>
          <TextField
            className={classnames(styles.value, {
              [styles.modalView]: isVariantCreateContactModal,
            })}
            size='small'
            variant='outline'
            InputProps={{
              placeholder: 'Email',
              autoComplete: 'off',
              ref: emailInputRef,
              onKeyDown: createMainFieldKeyDown(MainFiledTypes.email, emailInputRef),
            }}
            value={mainFields.email}
            disabled={disabledDefaultField || updatingFields.has(MainFiledTypes.email)}
            onChange={(value) => handleUpdateMainField(MainFiledTypes.email, value, true)}
            onBlur={(_, { isTrusted }) => {
              checkFirstEditMode(!isTrusted)
              saveMainField(MainFiledTypes.email, contact)
            }}
            rightActions={[
              makeTextFieldInteractions(
                { type: 'copy', active: Boolean(mainFields.email), zIndex: zIndex },
                { type: 'email', active: Boolean(mainFields.email), zIndex: zIndex }
              ),
              makeTextFieldClear(),
            ]}
            mainActions={[
              makeTextFieldAutoFocus({ withFocus: isFirstEditMode && firstEditInputNumber === 2 }),
              makeTextFieldTooltip({
                label: <TooltipEmailDomain email={mainFields.email} />,
                interactive: true,
                zIndex: zIndex,
              }),
              makeTextFieldActionTooltip({
                color: 'red',
                placement: 'left',
                active: errors.has('email'),
                message: errors.get('email'),
                onClick: () => errors.delete('email'),
                zIndex: zIndex,
              }),
            ]}
            ariaLabel={getAriaLabel(ariaLabelField, 'Value')}
          />
        </div>
      </div>

      <div className={styles.field} aria-label={ariaLabelField}>
        <span
          className={classnames(styles.label, {
            [styles.disabled]: isNewContactMode,
          })}
          aria-label={getAriaLabel(ariaLabelField, 'Label')}
        >
          <div className={styles.icon} aria-label={getAriaLabel('FieldType', 'userFilled')}>
            <Icon icon='userFilled' />
          </div>
          <Typography
            className={styles.name}
            variant='text-input-field-r'
            tag='div'
            ariaLabel={getAriaLabel(ariaLabelField, 'NameOwner')}
          >
            Owner
          </Typography>
        </span>

        <div
          className={classnames(styles.valueWrapper, {
            [styles.modalView]: isVariantCreateContactModal,
          })}
          aria-label={getAriaLabel(ariaLabelField, 'Value')}
        >
          <Dropdown
            ariaLabel={'ContactsInfoForm'}
            width={192}
            margin={-32}
            placement={'bottom'}
            typeButton={'icon-button'}
            disabled={disabledDefaultField || usersStore.user?.isViewOnlyRole}
            onClose={clearSearchOwner}
            zIndex={zIndex}
            triggerComponent={() => {
              if (updatingFields.has(MainFiledTypes.owner)) {
                return (
                  <span className={classnames(styles.value)}>
                    <div className={styles.icon}>
                      <SpinnerLoader ariaLabel={'ContactsInfoForm'} size={16} />
                    </div>
                    <Typography
                      className={styles.name}
                      variant='text-input-field-r'
                      pointer
                      ariaLabel={'Reassigning'}
                    >
                      Reassigning
                    </Typography>
                  </span>
                )
              }

              if (!mainFields.owner) {
                return (
                  <span
                    className={classnames(styles.value, styles.action, {
                      [styles.disabled]: isNewContactMode,
                    })}
                  >
                    <div className={styles.icon}>
                      <Icon icon='userFilled' />
                    </div>
                    <Typography
                      className={styles.name}
                      variant='text-input-field-r'
                      pointer
                      ariaLabel={'No owner'}
                    >
                      No owner
                    </Typography>
                  </span>
                )
              }

              return (
                <span className={classnames(styles.value, styles.action)}>
                  <div className={styles.icon}>
                    <Avatar
                      info={mainFields.owner.avatarInfo}
                      size={16}
                      online={!mainFields.owner.isAway}
                    />
                  </div>
                  <Typography
                    className={styles.name}
                    variant='text-input-field-r'
                    ellipsis
                    pointer
                    ariaLabel={'name'}
                  >
                    {mainFields.owner.name}
                  </Typography>
                </span>
              )
            }}
            customComponent={(onCloseMenu) => (
              <DropdownContent noPadding>
                <div className={styles.dropdownWrapper}>
                  <div className={styles.searchWrapper}>
                    <TextField
                      size='small'
                      variant='integrated'
                      InputProps={{
                        placeholder: 'Search',
                      }}
                      value={searchOwner}
                      onChange={handleSearchOwner}
                      mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
                    />
                  </div>
                  <div
                    className={classnames(styles.membersWrapper, {
                      [styles.withScroll]: membersCount > 5,
                    })}
                  >
                    {hasMembers ? (
                      <>
                        {membersList.map((member: IUser) => (
                          <OrganizationMember
                            handleOwner={(ownerId) => {
                              onCloseMenu()
                              handleSaveOwner(ownerId, contact)
                            }}
                            active={contact.owner_id === member.id}
                            key={member.id}
                            member={member}
                          />
                        ))}
                      </>
                    ) : (
                      <EmptyState noIcon />
                    )}
                  </div>
                </div>
              </DropdownContent>
            )}
          />
        </div>
      </div>

      {loadingCustomField
        ? isOpenBlock(blockViewKey) && (
            <div className={styles.field} aria-label={ariaLabelField}>
              <SpinnerLoader ariaLabel={'ContactsInfoForm'} size={24} />
            </div>
          )
        : isOpenBlock(blockViewKey) &&
          getVisibleCustomFieldsList(isVisibleOnlyCustomFields).map((customField) => (
            <CustomFieldField
              customField={customField}
              store={store}
              key={customField.field_key}
              ariaLabel={ariaLabelField}
            />
          ))}

      {hasCustomFields(isVisibleOnlyCustomFields) && (
        <div className={styles.showMore}>
          <Button
            size='small'
            typeBtn='text'
            text={isOpenBlock(blockViewKey) ? 'Hide fields' : 'See fields'}
            onClick={() => toggleBlockView(blockViewKey)}
          />
        </div>
      )}
    </div>
  )
})
